import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hammer, Send, AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { PageHeader, Panel, Spinner, Tag, Alert, EmptyState } from "@/components/common";
import { BudgetCap } from "@/components/BudgetCap";

const CampaignCard = ({ c, pages, planId, onSent }) => {
  const [sel, setSel] = useState(c.ad_groups.map((g) => g.suggested_page_id || ""));
  const [busy, setBusy] = useState(false);
  const byId = Object.fromEntries(pages.map((p) => [p.id, p]));
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/proposals/campaign", { plan_id: planId, campaign_index: c.index, page_ids: sel });
      toast.success(data.preflight?.length ? "Sent to approvals (currently blocked, see reasons there)" : "Sent to approvals");
      onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title={c.name} icon={Layers} testid={`builder-campaign-${c.index}`} right={c.proposal && <Tag tone="cyan" testid={`builder-proposal-status-${c.index}`}>Proposal: {c.proposal.status}</Tag>}>
      <div className="mb-4 font-mono text-xs text-slate-400">{fmtMoney(c.daily_budget)}/day · {fmtMoney(c.monthly_budget)}/month · {c.bidding}</div>
      <div className="space-y-3">{c.ad_groups.map((g, i) => {
        const page = byId[sel[i]];
        return (
          <div key={g.name} className="rounded-md border border-[#1e293b] p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm"><span className="font-semibold text-slate-200">{g.name}</span><span className="font-mono text-xs text-slate-500">{g.keywords} keywords · {g.headlines} headlines</span></div>
            <select data-testid={`builder-page-select-${c.index}-${i}`} className="cc-input mt-2 text-sm" value={sel[i]} onChange={(e) => setSel(sel.map((x, j) => (j === i ? e.target.value : x)))}>
              <option value="">Pick a landing page…</option>
              {pages.map((p) => <option key={p.id} value={p.id}>{`${p.title} (/lp/${p.slug})${p.owner_approved ? " ✓ approved" : " · awaiting approval"}`}</option>)}
            </select>
            {page && <div className="mt-1 break-all font-mono text-[11px] text-cyan-300" data-testid={`builder-final-url-${c.index}-${i}`}>{page.url}</div>}
            {page && !page.owner_approved && <div className="mt-1 flex gap-1.5 text-[11px] text-amber-300"><AlertTriangle className="h-3 w-3" /> Page not approved by owner yet: the proposal stays blocked until it is.</div>}
          </div>
        );
      })}</div>
      <button data-testid={`builder-send-${c.index}`} onClick={send} disabled={busy || sel.some((x) => !x)} className="cc-btn cc-btn-primary mt-4">{busy ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Send className="h-4 w-4" />} Send to approval queue</button>
    </Panel>
  );
};

export default function Builder() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/builder").then((r) => setData(r.data)).catch((e) => setError(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (error) return <EmptyState icon={Hammer} title="No plan yet" description={error} testid="builder-empty"><Link to="/plan" className="cc-btn cc-btn-primary">Go to Campaign Plan</Link></EmptyState>;
  if (!data) return <Spinner />;
  return (
    <div data-testid="builder-page">
      <PageHeader eyebrow="New campaigns" title="Campaign builder" description="Turn the AI plan into Google Ads Search campaigns. Each ad group points at one of your landing pages; final URLs use the landing page domain from Settings." />
      <div className="mb-6 font-mono text-xs text-slate-400" data-testid="builder-landing-base">Landing page domain: {data.landing_base.base} ({data.landing_base.source})</div>
      {data.live_domain_message && <div className="mb-6"><Alert tone="amber" title="Not executable yet" testid="builder-live-domain-alert">{data.live_domain_message}</Alert></div>}
      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="space-y-6">{data.campaigns.map((c) => <CampaignCard key={c.index} c={c} pages={data.pages} planId={data.plan_id} onSent={load} />)}</div>
        <BudgetCap b={data.budget} />
      </div>
    </div>
  );
}
